import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { cloudVersionsOf, createOwnRowSync, marksOf, mergeLoad, rowFocusRefresh, type OwnRowSync, type RowMarks } from '../lib/ownRowSync';
import { refreshBlobOnFocus } from '../lib/blobFocusRefresh';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';
import type { Folder, FolderCompletionRecord, ReplayMode, FolderDisplayMode, Task } from '../types';

// ⚠️⚠️ A DELETED FOLDER STAYS DELETED (2026-10-18): every write was an upsert, so a stale tab
// renaming, replaying or reordering folders re-inserted one deleted elsewhere. Writes now go
// through `lib/ownRowSync.ts`: only folders created here and unconfirmed (`unsyncedIds`) are
// inserted, every other write is an UPDATE, and a folder the cloud no longer has is dropped
// here. The load still replaces the cache with the cloud's folders, keeping only unsynced ones.
// ⚠️⚠️ AN EDIT THAT NEVER LANDED IS KEPT AND SAVED (2026-10-20): a rename, a replay, a reorder
// whose write never landed (offline, or a sign-out on its way) was lost on the next load. Now an
// unsaved edit of the folder as the cloud still has it is kept, and the load saves it
// (`writeBack`) — decided from what this device recorded, never a clock (lib/ownRowSync.ts); one of
// a folder another device has written since is merged onto that device's copy, from the base saved
// with it (`dirtyBases`, owner 2026-10-20). The
// row's `updated_at` is the SERVER's (trigger `update_task_folders_updated_at`), so comparing it
// with this device's `updatedAt` also re-wrote every saved folder while this clock ran ahead.

const FOLDERS_TABLE = 'task_folders';

/** The row's columns, without its id (an UPDATE never sets the id). */
function folderFields(folder: Folder, userId: string) {
  return {
    user_id: userId,
    name: folder.name,
    description: folder.description,
    category: folder.category,
    order: folder.order,
    replay_count: folder.replayCount,
    last_replayed_at: folder.lastReplayedAt,
    allow_xp_on_replay: folder.allowXpOnReplay,
    default_replay_mode: folder.defaultReplayMode,
    display_mode: folder.displayMode,
    completion_history: folder.completionHistory,
    updated_at: new Date().toISOString()
  };
}

function rowToFolder(row: any): Folder {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    category: row.category,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    order: row.order,
    replayCount: row.replay_count,
    lastReplayedAt: row.last_replayed_at,
    allowXpOnReplay: row.allow_xp_on_replay,
    defaultReplayMode: row.default_replay_mode,
    displayMode: row.display_mode || 'sequential',
    completionHistory: row.completion_history || []
  };
}

const folderSync: OwnRowSync<Folder> = createOwnRowSync<Folder>({
  table: FOLDERS_TABLE,
  label: 'task folders',
  fields: folderFields,
  current: (id) => useFolderStore.getState().folders.find((f) => f.id === id),
  fromRow: rowToFolder,
  replace: (folder) => useFolderStore.setState((s) => ({ folders: s.folders.map((f) => (f.id === folder.id ? folder : f)) })),
  unsyncedIds: () => useFolderStore.getState().unsyncedIds,
  confirm: (id) => useFolderStore.setState((s) =>
    s.unsyncedIds.includes(id) ? { unsyncedIds: s.unsyncedIds.filter((x) => x !== id) } : s),
  drop: (id) => useFolderStore.setState((s) => ({
    folders: s.folders.filter((f) => f.id !== id),
    unsyncedIds: s.unsyncedIds.filter((x) => x !== id),
  })),
  marks: () => useFolderStore.getState(),
  setMarks: (marks) => useFolderStore.setState(marks),
  name: (f) => f.name,
});

/**
 * Whose folders `loadFolders` last read from the cloud, and in which account generation
 * (lib/accountStorage): what a focus refresh may fold into. In memory — the saved copy persists
 * the whole state, and a page that hasn't loaded its folders hasn't loaded them.
 */
let loaded: { userId: string; at: number } | null = null;

interface FolderState extends RowMarks<Folder> {
  folders: Folder[];
  isLoading: boolean;
  error: string | null;
  /** Folders created on THIS device whose insert the cloud hasn't confirmed (see lib/ownRowSync.ts). Persisted. */
  unsyncedIds: string[];
  /** Whose folders the persisted cache holds (null: from before this field — a cloud copy). */
  cachedUserId: string | null;
  // `dirtyIds` + `cloudVersions` + `dirtyBases` (RowMarks, persisted): what a load decides by, without a clock — lib/ownRowSync.ts.
  addFolder: (folder: Omit<Folder, 'id' | 'createdAt' | 'updatedAt' | 'order' | 'replayCount' | 'allowXpOnReplay' | 'defaultReplayMode' | 'displayMode' | 'completionHistory'>) => Promise<string>;
  updateFolder: (id: string, updates: Partial<Folder>) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  reorderFolders: (sourceIndex: number, targetIndex: number) => Promise<void>;
  getFolderById: (id: string) => Folder | undefined;
  getFoldersByCategory: (category?: string) => Folder[];
  replayFolder: (folderId: string, tasks: Task[], mode: ReplayMode, allowXp: boolean, userLevel: number) => Promise<void>;
  updateReplaySettings: (folderId: string, allowXpOnReplay: boolean, defaultReplayMode: ReplayMode) => Promise<void>;
  getFolderProgress: (folderId: string, tasks: Task[]) => { completed: number; total: number; percentage: number };
  checkFolderCompletion: (folderId: string, tasks: Task[]) => boolean;
  loadFolders: () => Promise<void>;
  syncToSupabase: (folder: Folder) => Promise<void>;
}

export const useFolderStore = create<FolderState>()(
  persist<FolderState, [], [], any>(
    (set, get) => ({
      folders: [],
      isLoading: false,
      error: null,
      unsyncedIds: [],
      cachedUserId: null,
      dirtyIds: [],
      cloudVersions: {},
      dirtyBases: {},

      loadFolders: async () => {
        const at = accountGeneration();
        set({ isLoading: true, error: null });
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) {
            set({ isLoading: false });
            return;
          }

          const { result: { data, error }, win } = await folderSync.load(() => supabase
            .from(FOLDERS_TABLE)
            .select('*')
            .eq('user_id', user.id)
            .order('order', { ascending: true }));

          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const folders: Folder[] = (data || []).map(rowToFolder);

          let kept: string[] = [];
          set((state) => {
            // The cloud's folders replace the cache, as they always did — except folders
            // created here whose insert the cloud hasn't confirmed, and an unsaved edit of
            // a folder (merged onto it if written since). A cache with no owner yet is a cloud copy
            // (the load always replaced it).
            const ours = state.cachedUserId === null || state.cachedUserId === user.id;
            const merged = mergeLoad({
              cached: ours ? state.folders : [],
              unsyncedIds: ours ? state.unsyncedIds : [],
              marks: marksOf(state),
              cloud: folders,
              versions: cloudVersionsOf(data ?? []),
              win,
              label: 'task folders',
            });
            kept = merged.kept;
            return {
              folders: merged.rows.sort((a, b) => a.order - b.order),
              unsyncedIds: merged.unsyncedIds,
              ...merged.marks,
              cachedUserId: user.id,
              isLoading: false,
            };
          });
          loaded = { userId: user.id, at };
          folderSync.seen(folders, win); // the base of each folder's next edit (lib/ownRowSync.ts)
          void folderSync.writeBack(kept); // what the merge kept over the cloud (unsynced, or an unsaved edit)
        } catch (error: any) {
          console.error('Error loading folders:', error);
          set({ error: error.message, isLoading: false });
        }
      },

      // One save per folder through its queue (it sends the folder as it is THEN). Errors
      // are logged by lib/ownRowSync.ts; a folder deleted elsewhere is dropped, not re-created.
      syncToSupabase: async (folder: Folder) => {
        await folderSync.save(folder);
      },

      addFolder: async (folderData) => {
        const id = crypto.randomUUID();
        const folder: Folder = {
          ...folderData,
          id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          order: get().folders.length,
          replayCount: 0,
          allowXpOnReplay: true,
          defaultReplayMode: 'sequential',
          displayMode: 'sequential',
          completionHistory: []
        };

        set((state) => ({
          folders: [...state.folders, folder],
          unsyncedIds: [...state.unsyncedIds, folder.id], // ← INSERTED until the cloud confirms it
        }));

        await get().syncToSupabase(folder);
        return id;
      },

      updateFolder: async (id, updates) => {
        let updatedFolder: Folder | undefined;

        set((state) => {
          const folders = state.folders.map((folder) => {
            if (folder.id === id) {
              updatedFolder = {
                ...folder,
                ...updates,
                updatedAt: new Date().toISOString()
              };
              return updatedFolder;
            }
            return folder;
          });
          return { folders };
        });

        if (updatedFolder) {
          await get().syncToSupabase(updatedFolder);
        }
      },

      deleteFolder: async (id) => {
        folderSync.noteDeleted(id);
        set((state) => ({
          folders: state.folders.filter((folder) => folder.id !== id),
          unsyncedIds: state.unsyncedIds.filter((x) => x !== id),
        }));

        // ⚠️ After any save of this folder already on the wire: a delete that overtook the
        // folder's own insert would be undone by it.
        await folderSync.settle(id);
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;

          const { error } = await supabase
            .from(FOLDERS_TABLE)
            .delete()
            .eq('id', id)
            .eq('user_id', user.id);

          if (error) throw error;
        } catch (error: any) {
          console.error('Error deleting folder from Supabase:', error);
          set({ error: error.message });
        }
      },

      reorderFolders: async (sourceIndex, targetIndex) => {
        let reorderedFolders: Folder[] = [];

        set((state) => {
          const newFolders = [...state.folders];
          const [removed] = newFolders.splice(sourceIndex, 1);
          newFolders.splice(targetIndex, 0, removed);

          reorderedFolders = newFolders.map((folder, index) => ({
            ...folder,
            order: index,
            updatedAt: new Date().toISOString()
          }));

          return { folders: reorderedFolders };
        });

        for (const folder of reorderedFolders) {
          await get().syncToSupabase(folder);
        }
      },

      getFolderById: (id) => {
        return get().folders.find((folder) => folder.id === id);
      },

      getFoldersByCategory: (category) => {
        return get().folders
          .filter((folder) => !category || folder.category === category)
          .sort((a, b) => a.order - b.order);
      },

      getFolderProgress: (folderId, tasks) => {
        const folderTasks = tasks.filter(task => task.folderId === folderId);

        if (folderTasks.length === 0) {
          return { completed: 0, total: 0, percentage: 0 };
        }

        // Count LEAVES of the task tree (a parent with children is represented
        // by its children, not itself) so nesting doesn't inflate the count.
        // Children live in the same folder, so they're in `folderTasks` too.
        const leaves = folderTasks.filter(
          task => !folderTasks.some(other => other.parentTaskId === task.id),
        );

        const totalItems = leaves.length;
        const completedItems = leaves.filter(t => t.completed).length;
        const percentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

        return {
          completed: completedItems,
          total: totalItems,
          percentage
        };
      },

      checkFolderCompletion: (folderId, tasks) => {
        const folderTasks = tasks.filter(task => task.folderId === folderId);

        if (folderTasks.length === 0) return false;

        return folderTasks.every(task => {
          if (!task.completed) return false;

          if (task.subTasks && task.subTasks.length > 0) {
            return task.subTasks.every(st => st.completed);
          }

          return true;
        });
      },

      replayFolder: async (folderId, tasks, mode, allowXp, userLevel) => {
        const folder = get().getFolderById(folderId);
        if (!folder) {
          throw new Error('Folder not found');
        }

        const folderTasks = tasks.filter(task => task.folderId === folderId);

        if (folderTasks.length === 0) {
          throw new Error('Folder has no tasks to replay');
        }

        // ⭐⭐ A round no longer has to be finished (owner, 2026-09-17): a
        // grocery list is replayed whether 5 of 5 or 3 of 5 were bought. It does
        // need SOMETHING done — replaying an untouched list records nothing.
        const progress = get().getFolderProgress(folderId, tasks);
        if (progress.completed === 0) {
          throw new Error('Complete at least one task before replaying');
        }

        const completionRecord: FolderCompletionRecord = {
          timestamp: new Date().toISOString(),
          taskCount: progress.total,
          completedCount: progress.completed,
          userLevel
        };

        await get().updateFolder(folderId, {
          replayCount: folder.replayCount + 1,
          lastReplayedAt: new Date().toISOString(),
          completionHistory: [...folder.completionHistory, completionRecord],
          allowXpOnReplay: allowXp,
          defaultReplayMode: mode
        });
      },

      updateReplaySettings: async (folderId, allowXpOnReplay, defaultReplayMode) => {
        await get().updateFolder(folderId, {
          allowXpOnReplay,
          defaultReplayMode
        });
      }
    }),
    {
      name: 'folders-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 2,
      // The saved copy read back (a page load, a change of account): the base of each folder's next
      // edit — as this device last saw it in the cloud; an unsaved one's, its saved base (lib/ownRowSync.ts
      // `restore`). ⚠️ Runs inside `create`, before `useFolderStore` exists: the marks come with `state`.
      onRehydrateStorage: () => (state) => { if (state) folderSync.restore(state.folders, state); },
      migrate: (persistedState: any, version: number) => {
        if (version < 2) {
          return {
            folders: (persistedState.folders || []).map((folder: any) => ({
              ...folder,
              replayCount: folder.replayCount || 0,
              allowXpOnReplay: folder.allowXpOnReplay !== undefined ? folder.allowXpOnReplay : true,
              defaultReplayMode: folder.defaultReplayMode || 'sequential',
              displayMode: folder.displayMode || 'sequential',
              completionHistory: folder.completionHistory || []
            })),
            isLoading: false,
            error: null
          } as unknown as FolderState;
        }
        return persistedState as FolderState;
      }
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account saw the last
   one's folders until its own load; folders that lived only here were replaced by the next account's
   load; and the next account's folders made before its own load were kept by the last one's. */
scopeToAccount(useFolderStore);

// ⭐ A tab that comes back shows the folders the cloud holds now — read only (lib/ownRowSync.ts `rowFocusRefresh`),
// once `loadFolders` has read them for this account (`loaded`: the store has no `initializedUserId`).
refreshBlobOnFocus(FOLDERS_TABLE, rowFocusRefresh(folderSync, {
  label: 'task folders',
  loadedFor: () => (loaded?.at === accountGeneration() ? loaded.userId : null),
  held: () => useFolderStore.getState().folders,
  versions: (userId) => supabase.from(FOLDERS_TABLE).select('id, updated_at').eq('user_id', userId),
  apply: (read) => useFolderStore.setState((state) => {
    const merged = mergeLoad({ cached: state.folders, unsyncedIds: state.unsyncedIds, marks: marksOf(state), ...read.over(state.folders), label: 'task folders' });
    return { folders: merged.rows.sort((a, b) => a.order - b.order), unsyncedIds: merged.unsyncedIds, ...merged.marks };
  }),
}));
